import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import LanguageStore from '../store/LanguageStore';
import ChannelService from '../services/ChannelService';
import ChannelsTable from '../components/ChannelsTable/ChannelsTable';
import JsonLd from '../components/JsonLd/JsonLd';
import { createVideoListSchema } from '../utils/jsonLdSchemas';
import './channels-page.scss';

const ChannelsPage = observer(() => {
    const t = LanguageStore.t;
    const { language } = LanguageStore;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [channels, setChannels] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 25,
        totalPages: 1,
        totalItems: 0
    });
    const [sortBy, setSortBy] = useState('subscriberCount');
    const [sortOrder, setSortOrder] = useState('DESC');

    useEffect(() => {
        const fetchChannels = async () => {
            try {
                setLoading(true);
                const data = await ChannelService.getChannelList(
                    pagination.page,
                    pagination.limit,
                    sortBy,
                    sortOrder
                );
                setChannels(data.items);
                setPagination(data.pagination);
                setError(null);
            } catch (err) {
                console.error('Error fetching channels:', err);
                setError(err.response?.data?.error || 'Failed to load channels');
            } finally {
                setLoading(false);
            }
        };

        fetchChannels();
    }, [pagination.page, pagination.limit, sortBy, sortOrder]);

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            // Переключаем порядок сортировки
            setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
        } else {
            // Новая колонка для сортировки
            setSortBy(field);
            setSortOrder('ASC');
        }
        // Сбрасываем на первую страницу при изменении сортировки
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    if (loading && channels.length === 0) {
        return (
            <div className="channels-page">
                <div className="container">
                    <div className="channels-page__loading">
                        <div className="channels-page__spinner"></div>
                        <p>{t('loading') || 'Loading...'}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error && channels.length === 0) {
        return (
            <div className="channels-page">
                <div className="container">
                    <div className="channels-page__error">
                        <p>{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    const siteUrl = window.location.origin;
    const pageTitle = language === 'ru' ? 'Список каналов' : 'Channels List';
    const description = language === 'ru' 
        ? 'Список YouTube каналов с детальной статистикой подписчиков'
        : 'List of YouTube channels with detailed subscriber statistics';
    
    const channelListSchema = createVideoListSchema(siteUrl, pageTitle, description, language);

    return (
        <div className="channels-page">
            <JsonLd data={channelListSchema} id="json-ld-channel-list" />
            <article className="container">
                <h1 className="channels-page__title">
                    {pageTitle}
                </h1>

                <ChannelsTable
                    channels={channels}
                    pagination={pagination}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    onPageChange={handlePageChange}
                    loading={loading}
                />
            </article>
        </div>
    );
});

export default ChannelsPage;

